<template>
  <div ref="rootElement" class="mb-[8rem]">
    <!-- 상단 헤더 & 버튼 -->
    <div class="flex items-center justify-end mb-[4rem]">
      <button
        @click="handleReset"
        class="btn-secondary w-1/6 font-semibold text-green-400"
      >
        <i class="bi bi-arrow-repeat mr-2"></i>
        다시 분석하기
      </button>
    </div>

    <!-- 투자상품설명서 요약 및 목차 -->
    <div class="mb-[14rem] space-y-6">
      <h4 class="text-heading-2 font-semibold text-white pl-4">
        <i class="bi bi-1-square mr-2"></i>
        투자상품설명서 개요 및 목차
      </h4>

      <div class="bg-dark-700 rounded-2xl p-8 border border-dark-600 shadow-[0_20px_45px_rgba(0,0,0,0.35)] space-y-10">
        <!-- 문서 요약 -->
        <div class="pb-8 border-b-2 border-dark-600">
          <div class="flex items-center gap-2 text-base font-semibold uppercase tracking-[0.10em] text-gray-100 mb-4">
            <i class="bi bi-file-text text-blue-400 text-base"></i>
            문서 개요
          </div>
          <p class="text-gray-200 text-lg leading-relaxed bg-dark-800 rounded-xl p-8 py-8">
            {{ store.documentSummary || '문서 개요 정보가 없습니다.' }}
          </p>
        </div>

        <!-- 문서 목차 -->
        <div class="space-y-6">
          <div class="flex items-center gap-2 text-base font-semibold uppercase tracking-[0.10em] text-gray-100">
            <i class="bi bi-list-ul text-green-400 text-base"></i>
            목차 및 관련 내용
          </div>
          <div v-if="store.documentStructure && store.documentStructure.length > 0" class="bg-dark-800 border border-dark-600 rounded-xl overflow-hidden">
            <table class="w-full table-fixed">
              <colgroup>
                <col class="w-[10%]" />
                <col class="w-[35%]" />
                <col class="w-[55%]" />
              </colgroup>
              <thead class="bg-dark-600 border-b border-dark-500">
                <tr>
                  <th class="px-6 py-4 text-center text-sm font-semibold text-gray-400 uppercase tracking-wider">순서</th>
                  <th class="px-6 py-4 text-center text-sm font-semibold text-gray-400 uppercase tracking-wider">목차</th>
                  <th class="px-6 py-4 text-center text-sm font-semibold text-gray-400 uppercase tracking-wider">목차 내용</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-dark-600">
                <tr
                  v-for="section in store.documentStructure"
                  :key="section.id"
                  class="hover:bg-dark-700 transition-colors"
                >
                  <td class="px-6 py-4 text-center">
                    <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
                      <span class="text-primary font-semibold text-base">{{ section.id }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4">
                    <span class="text-white font-semibold text-base">{{ section.section_title }}</span>
                  </td>
                  <td class="px-6 py-4">
                    <p class="text-gray-300 text-base leading-relaxed">
                      {{ section.description }}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center text-gray-400 bg-dark-800 border border-dashed border-dark-500 rounded-xl py-12">
            문서 목차 정보가 없습니다.
          </div>
        </div>
      </div>
    </div>

    <!-- 검증 후보 목록 -->
    <div class="mb-[5rem] space-y-8">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <h4 class="text-heading-2 font-semibold text-white pl-4 flex items-center gap-3">
          <i class="bi bi-2-square"></i>
          검증 후보 문장 목록
          <div class="relative group ml-4">
            <span class="badge badge-secondary px-1.5 py-0.5 rounded border border-yellow-400 text-yellow-400 cursor-help text-xs hover:text-yellow-400/80 transition-colors inline-flex items-center gap-1">
              <i class="bi bi-question-circle text-yellow-400 text-xs"></i> 검증가이드
            </span>
            <!-- 툴팁 -->
            <div class="absolute left-0 bottom-full mb-2 w-[28rem] max-w-[calc(100vw-2rem)] bg-dark-700 rounded-lg p-6 border border-dark-600 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
              <div class="flex items-center gap-3 text-caption uppercase tracking-[0.2em] text-yellow-400 mb-4">
                <i class="bi bi-lightbulb text-yellow-400 text-lg"></i>
                검증 가이드라인
              </div>
              <div class="space-y-4 text-gray-300 text-sm leading-relaxed">
                <div class="flex items-start gap-3">
                  <span class="text-blue-400 font-semibold min-w-[2.5rem] text-center flex-shrink-0">1.</span>
                  <div class="flex-1 min-w-0">
                    <p class="text-white font-semibold mb-2">위험도(검증 시급도)</p>
                    <p class="text-gray-300">1(낮음) ~ 5(높음)까지 지정하여 중요도를 구분합니다.</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="text-blue-400 font-semibold min-w-[2.5rem] text-center flex-shrink-0">2.</span>
                  <div class="flex-1 min-w-0">
                    <p class="text-white font-semibold mb-2">검증 유형</p>
                    <div class="grid grid-cols-2 gap-2 text-gray-300">
                      <p>• 투자상품의 구조·조건</p>
                      <p>• 법령·규정</p>
                      <p>• 시장·산업의 수치·성과</p>
                      <p>• 투자대상기업의 수치·성과</p>
                      <p>• 시장 전망</p>
                      <p>• 운용사·인력</p>
                      <p>• 위험요소</p>
                    </div>
                  </div>
                </div>
              </div>
              <!-- 화살표 -->
              <div class="absolute left-4 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-dark-600"></div>
            </div>
          </div>
        </h4>
        <div class="bg-dark-700 border border-dark-600 rounded-xl px-6 py-4 flex flex-wrap items-center gap-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
          <p class="text-caption">
            {{ filteredCandidates.length }}개 중
            <strong class="text-primary">{{ store.selectedIds.length }}</strong>개 선택됨
          </p>
          <div class="flex items-center gap-3">
            <button
              @click="toggleSelectAll"
              class="btn-secondary text-sm"
            >
              <i class="bi bi-check-all mr-2"></i>
              {{ store.selectedIds.length === filteredCandidates.length ? '전체 해제' : '전체 선택' }}
            </button>
            <button
              @click="handleVerify"
              class="btn-primary text-sm"
            >
              <i class="bi bi-play-fill mr-2"></i>
              검증 시작
            </button>
          </div>
        </div>
      </div>


      <!-- 검증 유형별 탭 -->
      <div v-if="verificationTypes.length > 0" class="border-t-4 border-dark-600 -mx-5 px-5 flex flex-wrap gap-2 flex-shrink-0">
        <button
          v-for="type in verificationTypes"
          :key="type"
          :class="[
            'px-6 py-3 font-semibold text-base transition-all duration-200 border-b-4 relative',
            selectedType === type
              ? 'border-primary text-white bg-dark-700'
              : 'border-transparent text-gray-400 hover:text-white hover:bg-dark-700'
          ]"
          @click="selectedType = type"
        >
          {{ removeTypePrefix(type) }}
          <span
            v-if="getTypeCount(type) > 0"
            :class="[
              'ml-2 px-2 py-0.5 rounded-full text-xs',
              getTypeBadgeColor(type)
            ]"
          >
            {{ getTypeCount(type) }}
          </span>
        </button>
      </div>


      <!-- 카드 리스트 -->
      <div class="grid gap-8">
        <div
          v-for="candidate in filteredCandidates"
          :key="candidate.id"
          @click="store.toggleSelectedId(candidate.id)"
          :class="[
            'rounded-2xl border transition-all duration-200 cursor-pointer bg-dark-800/70 backdrop-blur-sm',
            store.selectedIds.includes(Number(candidate.id)) 
              ? 'border-primary/80 ring-2 ring-primary/30 shadow-[0_20px_40px_rgba(34,197,94,0.15)]' 
              : 'border-dark-600 hover:border-dark-400'
          ]"
        >
          <div class="p-6 flex flex-col gap-8">
            <!-- 메타 정보 -->
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div class="flex items-start gap-6">
                <div class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <span class="text-primary font-semibold text-base">{{ candidate.id }}</span>
                </div>
                <div class="space-y-1 flex-1">
                  <p class="text-caption uppercase tracking-[0.1em] text-gray-400">
                    <i class="bi bi-list-ul mr-1 text-blue-400"></i>목차
                  </p>
                  <p class="text-gray-200 text-base">{{ candidate.section_title }}</p>
                </div>
              </div>
              <div class="space-y-1">
                <p class="text-caption uppercase tracking-[0.1em] text-gray-400">
                  <i class="bi bi-check-circle mr-1 text-green-400"></i>검증유형
                </p>
                <span class="text-gray-200 text-base">
                  {{ removeTypePrefix(candidate.verification_type) }}
                </span>
              </div>
              <div class="space-y-1">
                <p class="text-caption uppercase tracking-[0.1em] text-gray-400">
                  <i class="bi bi-exclamation-triangle mr-1 text-red-400"></i>위험도(검증필요도)
                </p>
                <span :class="getRiskClass(candidate.risk_level)" class="text-base">
                  {{ candidate.risk_level }}
                </span>
              </div>
              <div class="space-y-1">
                <p class="text-caption uppercase tracking-[0.1em] text-gray-400">
                  <i class="bi bi-file-earmark-text mr-1 text-yellow-400"></i>문서페이지
                </p>
                <p class="text-gray-200 text-base">{{ candidate.page_number }}</p>
              </div>
            </div>

            <!-- 검증 문장: 목차부터 문서페이지까지 전체 차지 -->
            <div class="flex items-start gap-6">
              <div class="w-10 flex items-center justify-center flex-shrink-0">
                <input 
                  type="checkbox"
                  :checked="store.selectedIds.includes(Number(candidate.id))"
                  @click.stop="store.toggleSelectedId(candidate.id)"
                  class="rounded w-5 h-5 border-dark-500 bg-dark-900"
                />
              </div>
              <div class="flex-1 w-full min-w-0">
                <p class="text-caption uppercase tracking-[0.1em] text-gray-400">
                  <i class="bi bi-chat-left-text mr-1 text-blue-400"></i>검증문장
                </p>
                <p class="text-white text-base leading-relaxed font-semibold">
                  {{ candidate.verification_sentence }}
                </p>
              </div>
            </div>

            <!-- 검증이 필요한 이유 -->
            <div v-if="candidate.verification_need_reason || candidate.verifiability_reason" class="flex items-start gap-6">
              <div class="w-10 flex-shrink-0"></div>
              <div class="flex-1 w-full min-w-0 bg-dark-700/50 rounded-lg p-4">
                <p class="text-caption uppercase tracking-[0.1em] text-gray-400 mb-2">
                  <i class="bi bi-question-circle mr-1 text-yellow-400"></i>검증 필요 사유
                </p>
                <p class="text-gray-300 text-sm leading-relaxed">
                  {{ candidate.verification_need_reason || candidate.verifiability_reason }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <p
          v-if="!filteredCandidates || filteredCandidates.length === 0"
          class="text-center text-gray-400 bg-dark-700 border border-dashed border-dark-500 rounded-2xl py-12"
        >
          {{ selectedType === '전체' ? '검증 후보 문장이 없습니다.' : `'${selectedType}' 유형의 검증 후보 문장이 없습니다.` }}
        </p>
      </div>
    </div>

    <!-- 액션 버튼 -->
    <div class="flex items-center justify-between border-t-4 border-dark-600 pt-8">
      <!-- 왼쪽: 데이터 로드 -->
      <button
        @click="scrollToTop"
        class="btn-secondary"
      >
        <i class="bi bi-arrow-up mr-2"></i> 맨 위로 올라가기
      </button>

      <!-- 오른쪽: 선택 정보 및 버튼들 -->
      <div class="flex items-center gap-4">
        <p class="text-caption">
          {{ filteredCandidates.length }}개 중
          <strong class="text-primary">{{ store.selectedIds.length }}</strong>개 선택됨
        </p>
        <button
          @click="toggleSelectAll"
          class="btn-secondary"
        >
          <i class="bi bi-check-all mr-2"></i>
          {{ store.selectedIds.length === filteredCandidates.length ? '전체 해제' : '전체 선택' }}
        </button>
        <button
          :disabled="store.isLoading || store.selectedIds.length === 0"
          @click="handleVerify"
          class="btn-primary"
        >
          <span v-if="!store.isLoading">
            <i class="bi bi-play-fill mr-2"></i>검증 시작 ({{ store.selectedIds.length }}개)
          </span>
          <span v-else>
            <i class="bi bi-hourglass-split mr-2 animate-pulse"></i>
            검증 중...
          </span>
        </button>
      </div>     
    </div>

    <div class="flex justify-end mt-10">
        <button
        :disabled="store.isLoading"
        @click="handleLoadVerificationResult"
        class="btn-secondary"
        >
          <i class="bi bi-file-earmark-check mr-2"></i>
          JSON 데이터 로드
        </button>
      </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useVerificationStore } from '@/stores/verification';
import { verificationService } from '@/services/verification';
import { useToast } from 'vue-toastification';

const store = useVerificationStore();
const toast = useToast();
const rootElement = ref(null);

// 검증 유형별 탭 관리
const selectedType = ref('전체');

// 검증 유형 목록 추출
const verificationTypes = computed(() => {
  const types = new Set(['전체']);
  if (store.verificationCandidates && Array.isArray(store.verificationCandidates)) {
    store.verificationCandidates.forEach(candidate => {
      if (candidate && candidate.verification_type) {
        types.add(candidate.verification_type);
      }
    });
  }
  return Array.from(types);
});

// 선택된 유형별 필터링된 후보 목록
const filteredCandidates = computed(() => {
  if (!store.verificationCandidates || !Array.isArray(store.verificationCandidates)) {
    return [];
  }
  if (selectedType.value === '전체') {
    return store.verificationCandidates;
  }
  return store.verificationCandidates.filter(
    candidate => candidate && candidate.verification_type === selectedType.value
  );
});

// 유형별 개수 계산
const getTypeCount = (type) => {
  if (!store.verificationCandidates || !Array.isArray(store.verificationCandidates)) {
    return 0;
  }
  if (type === '전체') {
    return store.verificationCandidates.length;
  }
  return store.verificationCandidates.filter(
    candidate => candidate && candidate.verification_type === type
  ).length;
};

// 검증유형에서 알파벳과 점 제거 (예: "A. 투자상품" -> "투자상품")
const removeTypePrefix = (type) => {
  if (!type) return type;
  // 시작 부분의 알파벳, 점, 그리고 뒤따르는 공백 제거
  return type.replace(/^[A-Z]\.\s*/, '');
};

// 타입별 배지 색상 반환
const getTypeBadgeColor = (type) => {
  if (type === '전체') {
    return 'bg-gray-500/20 text-gray-400';
  }
  
  // 타입 이름의 첫 글자나 키워드에 따라 색상 결정
  const typeLower = type.toLowerCase();
  
  // 투자상품 관련
  if (typeLower.includes('투자상품') || typeLower.startsWith('a.')) {
    return 'bg-blue-500/20 text-blue-400';
  }
  // 운용 관련
  if (typeLower.includes('운용') || typeLower.startsWith('b.')) {
    return 'bg-purple-500/20 text-purple-400';
  }
  // 리스크 관련
  if (typeLower.includes('리스크') || typeLower.startsWith('c.')) {
    return 'bg-red-500/20 text-red-400';
  }
  // 수수료 관련
  if (typeLower.includes('수수료') || typeLower.startsWith('d.')) {
    return 'bg-yellow-500/20 text-yellow-400';
  }
  // 기타
  if (typeLower.startsWith('e.')) {
    return 'bg-emerald-500/20 text-emerald-400';
  }
  if (typeLower.startsWith('f.')) {
    return 'bg-cyan-500/20 text-cyan-400';
  }
  if (typeLower.startsWith('g.')) {
    return 'bg-orange-500/20 text-orange-400';
  }
  
  // 기본값: 타입 이름의 해시값을 기반으로 색상 결정
  const colors = [
    'bg-blue-500/20 text-blue-400',
    'bg-purple-500/20 text-purple-400',
    'bg-red-500/20 text-red-400',
    'bg-yellow-500/20 text-yellow-400',
    'bg-emerald-500/20 text-emerald-400',
    'bg-cyan-500/20 text-cyan-400',
    'bg-orange-500/20 text-orange-400',
    'bg-pink-500/20 text-pink-400',
    'bg-indigo-500/20 text-indigo-400',
  ];
  
  // 타입 이름의 해시값으로 색상 선택
  let hash = 0;
  for (let i = 0; i < type.length; i++) {
    hash = type.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getRiskClass = (level) => {
  const classes = {
    '5': 'text-red-200',
    '4': 'text-orange-200',
    '3': 'text-yellow-200',
    '2': 'text-green-200',
    '1': 'text-green-200',
  };
  return classes[String(level)] || 'text-gray-200';
};

const toggleSelectAll = () => {
  if (!filteredCandidates.value || filteredCandidates.value.length === 0) {
    return;
  }
  
  const currentFilteredIds = filteredCandidates.value
    .filter(c => c && c.id != null)
    .map(c => Number(c.id));
    
  if (currentFilteredIds.length === 0) {
    return;
  }
  
  const allSelected = currentFilteredIds.every(id => store.selectedIds.includes(id));
  
  if (allSelected) {
    // 현재 필터링된 항목들의 선택 해제
    currentFilteredIds.forEach(id => {
      const index = store.selectedIds.indexOf(id);
      if (index > -1) {
        store.selectedIds.splice(index, 1);
      }
    });
  } else {
    // 현재 필터링된 항목들 모두 선택
    currentFilteredIds.forEach(id => {
      if (!store.selectedIds.includes(id)) {
        store.selectedIds.push(id);
      }
    });
  }
};

const handleVerify = async () => {
  if (store.selectedIds.length === 0) {
    toast.error('검증할 문장을 선택해주세요');
    return;
  }

  // 검증 시작 안내 메시지
  toast.info('AI 검증을 시작합니다. 검증에는 최대 10분 정도 소요될 수 있습니다.', {
    timeout: 5000,
  });

  store.setLoading(true);

  try {

    console.log('store.selectedIds', store.selectedIds);
    console.log('store.jsonFilePath', store.jsonFilePath);

    const response = await verificationService.verifySentences(
      store.selectedIds,
      store.jsonFilePath
    );

    if (response.data.verification_results) {
      store.setVerificationResults(response.data.verification_results);
      toast.success('검증이 완료되었습니다.');
      store.setCurrentStep(2);
    }
  } catch (error) {
    console.error(error);
    toast.error('검증 중 오류가 발생했습니다. 다시 시도해주세요.');
  } finally {
    store.setLoading(false);
  }
};

const handleLoadVerificationResult = async () => {
  try {
    // import로 JSON 파일 직접 가져오기
    const testData = await import('@/assets/results/verify_result.json');
    const data = testData.default;

    if (data && data.length > 0) {
      store.setVerificationResults(data);
      store.setCurrentStep(2);
      toast.success('검증완료 데이터가 로드되었습니다.');
    }
  } catch (error) {
    console.error(error);
    toast.error('데이터 로드 실패: ' + error.message);
  }
};

const handleReset = () => {
  store.reset();
};

const scrollToTop = () => {
  // rootElement를 통해 부모 요소 중 overflow-auto를 가진 요소 찾기
  if (rootElement.value) {
    let parent = rootElement.value.parentElement;
    while (parent && parent !== document.body) {
      // overflow-auto 클래스를 가진 요소 찾기
      if (parent.classList && parent.classList.contains('overflow-auto')) {
        parent.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // className 문자열에서도 확인
      const className = parent.className || '';
      if (typeof className === 'string' && className.includes('overflow-auto')) {
        if (parent.scrollTo) {
          parent.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          parent.scrollTop = 0;
        }
        return;
      }
      parent = parent.parentElement;
    }
  }
  
  // fallback: 직접 overflow-auto 요소 찾기
  const overflowElements = document.querySelectorAll('.overflow-auto');
  if (overflowElements.length > 0) {
    const container = overflowElements[0];
    if (container.scrollTo) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      container.scrollTop = 0;
    }
    return;
  }
  
  // 최종 fallback: main과 window
  const mainElement = document.querySelector('main');
  if (mainElement && mainElement.scrollTo) {
    mainElement.scrollTo({ top: 0, behavior: 'smooth' });
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>

<style scoped>
</style>
